import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Chip,
  Alert,
  Stack,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import { CheckCircle, Warning, Cancel, Receipt } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '@/app/store';
import { useAuth } from '@/hooks/useAuth';
import PageHeader from '@/components/common/PageHeader';
import TableList, { TableColumn } from '@/components/common/TableList';
import LoadingOverlay from '@/components/common/LoadingOverlay';
import { fetchFilteredPurchases } from '@/features/payments/paymentsThunks';
import {
  selectPayments,
  selectPaymentsLoading,
  selectPaymentsError,
  selectPagination
} from '@/features/payments/paymentsSelectors';
import { Payment, PurchaseStatus, PaymentConcept } from '@/types/payment.types';
import { setPageSize, setPayments, resetPagination } from '@/features/payments/paymentsSlice';

const MyPurchases: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser } = useAuth();
  const purchases = useSelector(selectPayments);
  const isLoading = useSelector(selectPaymentsLoading);
  const error = useSelector(selectPaymentsError);
  const pagination = useSelector(selectPagination);
  const [page, setPage] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState<Payment | null>(null);

  useEffect(() => {
    setPage(pagination.currentPage - 1);
  }, [pagination.currentPage]);

  useEffect(() => {
    if (currentUser?.identifier) {
      // Limpiar el estado de compras y paginación cuando cambia el usuario
      dispatch(setPayments([]));
      dispatch(resetPagination());

      // Cargar las compras del nuevo usuario
      dispatch(
        fetchFilteredPurchases({
          filters: {
            dateRange: { start: null, end: null },
            status: null,
            userId: currentUser.identifier,
            userName: null,
            paymentItem: null,
          },
          pageSize: pagination.pageSize || 10,
        })
      );
    }
  }, [dispatch, currentUser?.identifier]);

  const handlePageChange = (newPage: number) => {
    const direction = newPage > page ? 'next' : 'previous';

    if (currentUser?.id) {
      dispatch(
        fetchFilteredPurchases({
          filters: {
            dateRange: { start: null, end: null },
            status: null,
            userId: currentUser.identifier,
            userName: null,
            paymentItem: null,
          },
          pageSize: pagination.pageSize,
          direction,
          lastVisible: direction === 'next' ? pagination.lastVisible : undefined,
          firstVisible: direction === 'previous' ? pagination.firstVisible : undefined,
        })
      );
    }
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    dispatch(setPageSize(newRowsPerPage));

    if (currentUser?.id) {
      dispatch(
        fetchFilteredPurchases({
          filters: {
            dateRange: { start: null, end: null },
            status: null,
            userId: currentUser.identifier,
            userName: null,
            paymentItem: null,
          },
          pageSize: newRowsPerPage,
        })
      );
    }
  };

  const handleOpenModal = (purchase: Payment) => {
    setSelectedPurchase(purchase);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedPurchase(null);
  };

  const getStatusColor = (status: PurchaseStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case PurchaseStatus.PENDING:
        return 'warning';
      case PurchaseStatus.COMPLETED:
      case PurchaseStatus.APPROVED:
        return 'success';
      case PurchaseStatus.FAILED:
      case PurchaseStatus.REJECTED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: PurchaseStatus) => {
    switch (status) {
      case PurchaseStatus.PENDING:
        return <Warning fontSize="small" />;
      case PurchaseStatus.COMPLETED:
      case PurchaseStatus.APPROVED:
        return <CheckCircle fontSize="small" />;
      case PurchaseStatus.FAILED:
      case PurchaseStatus.REJECTED:
        return <Cancel fontSize="small" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status: PurchaseStatus): string => {
    switch (status) {
      case PurchaseStatus.PENDING:
        return 'Pendiente';
      case PurchaseStatus.COMPLETED:
        return 'Completado';
      case PurchaseStatus.APPROVED:
        return 'Aprobado';
      case PurchaseStatus.FAILED:
        return 'Fallido';
      case PurchaseStatus.REJECTED:
        return 'Rechazado';
      default:
        return 'Desconocido';
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const columns: TableColumn<Payment>[] = [
    {
      key: 'createdAt',
      label: 'Fecha y Hora',
      render: (payment) => {
        const date = new Date(payment.createdAt);
        const dateStr = date.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
        const timeStr = date.toLocaleTimeString('es-ES', {
          hour: '2-digit',
          minute: '2-digit'
        });
        return `${dateStr} ${timeStr}`;
      }
    },
    {
      key: 'referenceCode',
      label: 'Referencia',
      hideOn: { xs: true, sm: true },
    },
    {
      key: 'description',
      label: 'Detalles',
      render: (payment) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleOpenModal(payment)}
          sx={{
            textTransform: 'none',
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          }}
        >
          Ver
        </Button>
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      render: (payment) => (
        <Chip
          icon={getStatusIcon(payment.status) || undefined}
          label={getStatusLabel(payment.status)}
          color={getStatusColor(payment.status)}
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: { xs: '0.7rem', sm: '0.75rem' },
            height: { xs: 24, sm: 28 }
          }}
        />
      ),
    },
    {
      key: 'amount',
      label: 'Total',
      align: 'right',
      render: (payment) => formatCurrency(payment.amount),
      cellSx: {
        fontWeight: 600,
        fontSize: { xs: '0.85rem', sm: '0.875rem' },
        color: 'primary.main'
      }
    },
  ];

  const paginationCount = pagination.hasNextPage
    ? -1
    : page * pagination.pageSize + purchases.length;

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 6 }}>
        <Alert severity="error">
          Error al cargar las compras: {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="xl"
      sx={{
        pb: 6,
        px: { xs: 2, md: 3, lg: 4 }
      }}
    >
      <PageHeader
        title="Mis Compras"
        description="Consulta el historial completo de tus pagos y transacciones realizadas en el sistema."
      />

      <Box sx={{ px: { xs: 0, md: 2, lg: 8 } }}>
        {/* Summary Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: 3,
            mb: 4,
          }}
        >
          <Card elevation={0} sx={{ border: '1px solid #E0E0E0', borderRadius: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    bgcolor: '#E3F2FD',
                    p: 1.5,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Receipt sx={{ color: '#1976D2', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total de Compras
                  </Typography>
                  <Typography variant="h5" fontWeight={600}>
                    {purchases.length}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ border: '1px solid #E0E0E0', borderRadius: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    bgcolor: '#E8F5E9',
                    p: 1.5,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CheckCircle sx={{ color: '#2E7D32', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Completadas
                  </Typography>
                  <Typography variant="h5" fontWeight={600}>
                    {purchases.filter(p => p.status === PurchaseStatus.COMPLETED || p.status === PurchaseStatus.APPROVED).length}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ border: '1px solid #E0E0E0', borderRadius: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    bgcolor: '#FFF3E0',
                    p: 1.5,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Warning sx={{ color: '#F57C00', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Pendientes
                  </Typography>
                  <Typography variant="h5" fontWeight={600}>
                    {purchases.filter(p => p.status === PurchaseStatus.PENDING).length}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ border: '1px solid #E0E0E0', borderRadius: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    bgcolor: '#FFEBEE',
                    p: 1.5,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Cancel sx={{ color: '#C62828', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Fallidas
                  </Typography>
                  <Typography variant="h5" fontWeight={600}>
                    {purchases.filter(p => p.status === PurchaseStatus.FAILED || p.status === PurchaseStatus.REJECTED).length}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Purchases Table */}
        {isLoading && <LoadingOverlay message="Cargando compras..." />}
        <TableList
          data={purchases}
          columns={columns}
          title="Historial de Compras"
          getRowKey={(payment) => payment.id}
          enablePagination={true}
          page={page}
          rowsPerPage={pagination.pageSize}
          totalCount={paginationCount}
          hasNextPage={pagination.hasNextPage}
          hasPreviousPage={pagination.hasPreviousPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          emptyMessage="No hay compras registradas. Tus compras aparecerán aquí una vez que realices tu primera transacción."
        />
      </Box>

      {/* Purchase Details Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
          Detalles de la Compra
        </DialogTitle>
        <DialogContent>
          {selectedPurchase && (
            <Box sx={{ pt: 2 }}>
              <Stack spacing={3}>
                {/* Header with Status */}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={2}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Referencia
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {selectedPurchase.referenceCode}
                    </Typography>
                  </Box>
                  <Chip
                    icon={getStatusIcon(selectedPurchase.status) || undefined}
                    label={getStatusLabel(selectedPurchase.status)}
                    color={getStatusColor(selectedPurchase.status)}
                    size="medium"
                  />
                </Stack>

                <Divider />

                {/* Purchase Information */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 3,
                  }}
                >
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Fecha de compra
                      </Typography>
                      <Typography variant="body2">
                        {new Date(selectedPurchase.createdAt).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Monto total
                      </Typography>
                      <Typography variant="h6" color="primary" fontWeight={600}>
                        {formatCurrency(selectedPurchase.amount)}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Comprador
                      </Typography>
                      <Typography variant="body2">
                        {selectedPurchase.customerName}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Correo electrónico
                      </Typography>
                      <Typography variant="body2">
                        {selectedPurchase.customerEmail}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* Concepts Section */}
                {selectedPurchase.concepts && selectedPurchase.concepts.length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        Conceptos de pago
                      </Typography>
                      <Stack spacing={1.5} sx={{ mt: 2 }}>
                        {selectedPurchase.concepts.map((concept: PaymentConcept, index: number) => (
                          <Box
                            key={`${concept.id}-${index}`}
                            sx={{
                              p: 2,
                              bgcolor: '#F5F5F5',
                              borderRadius: 1,
                            }}
                          >
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {concept.name}
                                </Typography>
                                {concept.studentId && (
                                  <Typography variant="caption" color="text.secondary">
                                    ID Estudiante: {concept.studentId}
                                  </Typography>
                                )}
                              </Box>
                              <Typography variant="body2" fontWeight={600}>
                                {formatCurrency(concept.amount)}
                              </Typography>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </>
                )}

                {/* Description */}
                {selectedPurchase.description && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Descripción
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {selectedPurchase.description}
                      </Typography>
                    </Box>
                  </>
                )}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseModal}
            variant="contained"
            sx={{
              textTransform: 'none',
              px: 3
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyPurchases;
